import { supabase } from '../lib/supabase'

export interface EmailPreferences {
  marketing_emails: boolean
  booking_confirmations: boolean
  booking_reminders: boolean
  receipts_invoices: boolean
  class_updates: boolean
  membership_updates: boolean
  newsletters: boolean
  promotional_offers: boolean
  unsubscribe_token?: string
}

export interface EmailCategory {
  type: 'marketing' | 'transactional' | 'booking' | 'membership' | 'newsletter' | 'promotional'
  preference_key: keyof EmailPreferences
}

// Map email types to preference keys
export const EMAIL_CATEGORIES: Record<string, EmailCategory> = {
  BOOKING_CONFIRMATION: { type: 'booking', preference_key: 'booking_confirmations' },
  BOOKING_REMINDER: { type: 'booking', preference_key: 'booking_reminders' },
  RECEIPT: { type: 'transactional', preference_key: 'receipts_invoices' },
  INVOICE: { type: 'transactional', preference_key: 'receipts_invoices' },
  CLASS_UPDATE: { type: 'booking', preference_key: 'class_updates' },
  CLASS_CANCELLATION: { type: 'booking', preference_key: 'class_updates' },
  MEMBERSHIP_RENEWAL: { type: 'membership', preference_key: 'membership_updates' },
  MEMBERSHIP_EXPIRY: { type: 'membership', preference_key: 'membership_updates' },
  NEWSLETTER: { type: 'newsletter', preference_key: 'newsletters' },
  PROMOTION: { type: 'promotional', preference_key: 'promotional_offers' },
  MARKETING: { type: 'marketing', preference_key: 'marketing_emails' },
}

/**
 * Check if a client wants to receive a specific type of email
 */
export async function checkEmailPreference(
  clientId: string, 
  emailType: keyof typeof EMAIL_CATEGORIES
): Promise<boolean> {
  try {
    // Get client's email preferences
    const { data: prefs, error } = await supabase
      .from('email_preferences')
      .select('*')
      .eq('client_id', clientId)
      .single()

    if (error || !prefs) {
      // If no preferences found, default to true (opt-in by default)
      console.warn('No email preferences found for client:', clientId)
      return true
    }

    const category = EMAIL_CATEGORIES[emailType]
    if (!category) {
      console.warn('Unknown email type:', emailType)
      return true
    }

    // Transactional emails are always sent
    if (category.type === 'transactional') {
      return true
    }

    // Check the specific preference
    return prefs[category.preference_key] === true
  } catch (error) {
    console.error('Error checking email preferences:', error)
    // Default to sending if there's an error
    return true
  }
}

/**
 * Generate unsubscribe link for a client
 */
export async function generateUnsubscribeLink(clientId: string): Promise<string | null> {
  try {
    // Get the unsubscribe token
    const { data, error } = await supabase
      .from('email_preferences')
      .select('unsubscribe_token')
      .eq('client_id', clientId)
      .single()

    if (error || !data?.unsubscribe_token) {
      console.error('No unsubscribe token found for client:', clientId)
      return null
    }

    // Generate the unsubscribe URL
    const baseUrl = window.location.origin
    return `${baseUrl}/unsubscribe/${data.unsubscribe_token}`
  } catch (error) {
    console.error('Error generating unsubscribe link:', error)
    return null
  }
}

/**
 * Add unsubscribe footer to email HTML
 */
export async function addUnsubscribeFooter(
  html: string, 
  clientId: string,
  isMarketingEmail: boolean = false
): Promise<string> {
  if (!isMarketingEmail) {
    return html
  }

  const unsubscribeLink = await generateUnsubscribeLink(clientId)
  if (!unsubscribeLink) {
    return html
  }

  const footer = `
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e5e5; text-align: center; font-size: 12px; color: #666;">
      <p style="margin: 0 0 10px 0;">
        You're receiving this email because you're subscribed to our mailing list.
      </p>
      <p style="margin: 0;">
        <a href="${unsubscribeLink}" style="color: #666; text-decoration: underline;">
          Unsubscribe
        </a>
        |
        <a href="${unsubscribeLink}" style="color: #666; text-decoration: underline;">
          Manage Preferences
        </a>
      </p>
    </div>
  `

  // Insert footer before closing body tag
  if (html.includes('</body>')) {
    return html.replace('</body>', footer + '</body>')
  }
  
  // Or just append if no body tag
  return html + footer
}

/**
 * Check if email should be sent based on preferences
 */
export async function shouldSendEmail(
  clientId: string,
  emailType: keyof typeof EMAIL_CATEGORIES
): Promise<{ send: boolean; reason?: string }> {
  // Always send transactional emails
  const category = EMAIL_CATEGORIES[emailType]
  if (category?.type === 'transactional') {
    return { send: true }
  }

  // Check preferences
  const canSend = await checkEmailPreference(clientId, emailType)
  
  if (!canSend) {
    return { 
      send: false, 
      reason: `Client has opted out of ${category?.type || 'this type of'} emails` 
    }
  }

  return { send: true }
}